import { default as Enumerable } from "linq";
import store from "../../../store";
import { GeometryManager } from "../../../components/system/utilities";
import { SessionStorageAccessor } from "../../../store/SessionStorageAccessor";
import { ILogger, LoggerFactory } from "../../../utilities/logger";
import { WindowCloser, WindowManager } from "../../../system";
import { Route, RawLocation } from "vue-router";
import { ExtendedRouter } from "./ExtendedRouter";
import { Query } from "./Query";

export class GlobalDispatcher<TQuery extends Query> {
  constructor(private readonly _router: ExtendedRouter, public readonly toQuery: (r: Route) => TQuery) {
    this._additionalDispatchers = [];
    this._additionalDispatchersBeforePrefetch = [];
  }

  private readonly _additionalDispatchers: ((to: Route, from: Route) => Promise<RawLocation | false | void>)[];
  private readonly _additionalDispatchersBeforePrefetch: ((to: Route, from: Route) => Promise<RawLocation | false | void>)[];

  protected addDispatcher(dispatcher: (to: Route, from: Route) => Promise<RawLocation | false | void>, isBeforePrefetch: boolean): void {
    this.addDispatchers([ dispatcher ], isBeforePrefetch);
  }

  protected addDispatchers(dispatchers: ((to: Route, from: Route) => Promise<RawLocation | false | void>)[], isBeforePrefetch: boolean): void {
    if (isBeforePrefetch) {
      Array.prototype.push.apply(this._additionalDispatchersBeforePrefetch, dispatchers);
    } else {
      Array.prototype.push.apply(this._additionalDispatchers, dispatchers);
    }
  }

  public async dispatch(to: Route, from: Route, next: (to?: RawLocation | false | void) => void): Promise<void> {
    this.logger.info("ルーティング開始.");
    this.logger.info(to.fullPath);
    this.logger.debug("from " + from.fullPath);
    this.storeEntryRoute(to);
    this.storeIsManualCloseable(this.toQuery(to));
    this.initializeWindowManager(this.toQuery(to), to.meta);
    this.updateWindowTitle(this.toQuery(to).windowTitle);
    if (await this.redirect(to, from, next, (t, f) => this.suspendConsecutiveErrors(t, f))) {
      return;
    }
    this.setupStore(to);
    if (await this.redirect(to, from, next, (t, f) => this.blockRestored(t))) {
      return;
    }
    if (await this.redirect(to, from, next, (t, f) => this.authenticate(t, f))) {
      return;
    }
    if (await this.redirect(to, from, next, this._additionalDispatchersBeforePrefetch)) {
      return;
    }
    this._router.prefetch(to, from);
    if (await this.redirect(to, from, next, this._additionalDispatchers)) {
      return;
    }
    this.logger.info("ルーティングされた画面へ.");
    this.resetCloseConfirmation();
    this.setupWindowRectangle(to);
    next(); // リダイレクト先が指定されなかったので予定していた遷移先へ.
  }

  private storeEntryRoute(to: Route): void {
    if (!SessionStorageAccessor.entryFullPath) {
      SessionStorageAccessor.entryFullPath = to.fullPath;
    }
  }

  private storeIsManualCloseable(query: TQuery): void {
    if (query.isManualCloseable != null) {
      SessionStorageAccessor.isManualCloseable = query.isManualCloseable;
    }
  }

  private initializeWindowManager(query: TQuery, meta: any): void {
    if (query.launchedBy) {
      SessionStorageAccessor.launchedInformation = {
        by: query.launchedBy,
        patientNo: query.launchedPatientNo,
      };
    }
    const isBackground = meta && meta.isBackground;
    WindowManager.instance.initialize(SessionStorageAccessor.launchedInformation, isBackground);
  }

  protected async redirect(to: Route, from: Route, next: (to?: RawLocation | false | void) => void, toDestinations: ((to: Route, from: Route) => Promise<RawLocation | false | void>) | (((to: Route, from: Route) => Promise<RawLocation | false | void>)[])): Promise<boolean> {
    if (!Array.isArray(toDestinations)) {
      return this.redirect(to, from, next, [ toDestinations ]);
    }
    try {
      for (const toDestination of toDestinations) {
        const destination = await toDestination(to, from);
        if ((destination !== undefined) && (destination !== null)) {
          next(destination);
          return true;
        }
      }
      return false;
    } catch (e) {
      next(this.toError(to, e.toString()));
      return true;
    }
  }

  protected toError(to: Route, message: string): RawLocation {
    return {
      path: "/Error",
      query: {
        url: to.fullPath,
        message,
      }
    };
  }

  protected toWarning(to: Route, message: string): RawLocation {
    return {
      path: "/Warning",
      query: {
        url: to.fullPath,
        message,
      }
    };
  }

  protected get logger(): ILogger {
    return LoggerFactory.getLogger();
  }

  private async suspendConsecutiveErrors(to: Route, from: Route): Promise<RawLocation | false | void> {
    if (to.path === "/Error") {
      if (from.path === "/Error") {
        // replaceしてるので遷移前の画面で連続してエラーが発生した場合、エラー画面からの遷移になる模様.
        // 先のエラーを優先して表示する.
        this.logger.warn("Error画面表示中の更なるエラー.最初の表示優先でルーティング中断.");
        return false;
      }
    } else {
      if (!store.getters.configuration) {
        return this.toError(to, "設定が取得できません.");
      }
    }
  }

  private setupStore(to: Route): void {
    if (to.query.disableCloseConfirm === true.toString()) {
      if (store.getters.configuration) {
        store.getters.configuration.behavior.document.editor.shouldConfirmToLeavePage = false;  // 一時的にCloseConfirmを無効にする.
      }
    }
    if (to.matched.some(record => record.meta.isMaintenance)) {
      // メンテナンスサイトを開くときに都合が悪い設定を修正する
      if (store.getters.configuration) {
        // メンテナンス使用者の診療科が診療科グループにより絞り込みされてしまうので無効にしている
        store.getters.configuration.behavior.departmentGroup.feature.isEnable = false;
        // 診療科グループ指定が無くても起動できるようにしている
        store.getters.configuration.behavior.departmentGroup.scope.isEnable = false;
      }
    }
    if (!store.getters.token && SessionStorageAccessor.token && SessionStorageAccessor.user) {
      store.commit("setToken", SessionStorageAccessor.token);
      store.commit("setUser", SessionStorageAccessor.user);
      store.commit("setDepartment", SessionStorageAccessor.department);
      store.commit("setUserAuthority", SessionStorageAccessor.userAuthority);
      store.commit("setUserDepartments", SessionStorageAccessor.userDepartments);
      store.commit("setOrderUser", SessionStorageAccessor.orderUser);
      store.commit("setAttendingUser", SessionStorageAccessor.attendingUser);
      store.commit("setExtraConfiguration", SessionStorageAccessor.extraConfiguration);
      store.commit("setDepartmentGroup", SessionStorageAccessor.departmentGroup);
      store.commit("setReferenceableDepartmentGroups", SessionStorageAccessor.referenceableDepartmentGroups);
      store.commit("setIsDepartmentGroupScopeEnable", SessionStorageAccessor.isDepartmentGroupScopeEnable);
      store.commit("setUserSelection", SessionStorageAccessor.userSelection);
    }
    if (SessionStorageAccessor.isManualCloseable != null) {
      store.commit("setIsManualCloseable", SessionStorageAccessor.isManualCloseable);
    }
    if (to.query.clearDepartment === true.toString()) {
      store.commit("setDepartment", null);
    }
  }

  private async authenticate(to: Route, from: Route): Promise<RawLocation | false | void> {
    // ログイン画面へのリダイレクト.下記参考.
    // https://qiita.com/takatama/items/05e9fbc7199cde4caf60
    if (to.matched.some(record => record.meta.requiresAuth) && !this.isAlreadyLogined(this.toQuery(to))) {
      const query: any = {
        userCode: this.toQuery(to).userCode,
        departmentCode: this.toQuery(to).departmentCode,
        hostName: this.toQuery(to).hostName,
        orderUserCode: this.toQuery(to).orderUserCode,
        attendingUserCode: this.toQuery(to).attendingUserCode,
        shouldSelectDepartment: this.toQuery(to).shouldSelectDepartment === undefined ? undefined : (!!this.toQuery(to).shouldSelectDepartment).toString(),
        departmentGroupCode: this.toQuery(to).departmentGroupCode,
        referenceableDepartmentGroupCodes: !!this.toQuery(to).referenceableDepartmentGroupCodes ? this.toQuery(to).referenceableDepartmentGroupCodes.join(",") : undefined,
      };
      const redirectUrl = this.trimLoginInformation(to.fullPath);
      if (redirectUrl && redirectUrl !== "/") {
        query.redirect = redirectUrl;
      }
      this.logger.info("ログイン画面へリダイレクト.");
      return {
        path: "/Login",
        query,
        replace: (from.path !== "/Welcome") // historyを追加せずに遷移.開発中フロントエンドには戻れた方がいいので暫定対応.(TODO)
      };
    }
  }

  private isAlreadyLogined(query: TQuery): boolean {
    // 違うユーザ/診療科が指定されてる場合はログイン未として扱う.
    return store.getters.token
        && (!query.userCode || (store.getters.user && (store.getters.user.id === query.userCode)))
        && (!query.departmentCode || (store.getters.department && (store.getters.department.id === query.departmentCode)));
  }

  private trimLoginInformation(source: string): string {
    if (!source) {
      return source;
    }
    const values = source.split("?");
    if (values.length < 2) {
      return source;
    }
    return values[0] + "?" + Enumerable.from(values[1].split("&")).where(p => !this.isLoginParameter(p)).toArray().join("&");
  }

  private async blockRestored(to: Route): Promise<RawLocation | false | void> {
    // iPadのSafariでショートカットで必ず指定されてる診療科が指定されないで文書登録されるケースあり.
    // Safariのタスクを終了するとSafariがページを復元しようとするようでそこからログインして書いたものとの事.
    // sessionstorageに待避してURLからは削っている診療科(ユーザも)コードが復元されないので発生してる模様.
    // ログイン診療科コードをURLから削らないのも考えたが以下の理由からやりたくない.
    // ・元々URLにログインに関する情報が出るのはセキュリティ的によろしくないので消してる.
    // ・他にも同様の対応するものが増える可能性もあり変な制約は付けたくない.
    // 以下の通り問題の復元動作をエラーとして扱うことで対策とする.
    // ・復元ではない事を示す必須のパラメータ(isNotRestored)を設ける.
    // ・指定されてた場合はsessionstorageに復元でない事を記録しURLからパラメータを除去する.
    // ・未指定かつsessionstorageにも記録されてない場合は復元と見なしてエラーへ遷移させる.
    if (this.toQuery(to).isNotRestored) {
      SessionStorageAccessor.isNotRestored = true;
      const query = to.query;
      delete query.isNotRestored;
      return {
        path: to.path,
        query,
        replace: true,
      };
    } else if (!SessionStorageAccessor.isNotRestored && this.isUnrestorableRoute(to)) {
      return this.toWarning(to, "復元による画面遷移は禁止されています.");
    }
  }

  private isUnrestorableRoute(route: Route): boolean {
    return (!route.meta || !route.meta.isAlwaysRestorable)
        && Enumerable.from(store.getters.configuration.routing.unrestorableRouteExpressions as string[]).select(e => new RegExp(e)).any(e => e.test(route.path));
  }

  private isLoginParameter(parameter: string): boolean {
    const lower = parameter.toLowerCase();
    return lower.startsWith("usercode=")
        || lower.startsWith("userid=")
        || lower.startsWith("departmentcode=")
        || lower.startsWith("departmentid=")
        || lower.startsWith("hostname=")
        || lower.startsWith("disablecloseconfirm=")
        || lower.startsWith("orderusercode=")
        || lower.startsWith("attendingusercode=")
        || lower.startsWith("clientipaddress=")
        || lower.startsWith("shouldselectdepartment=")
        || lower.startsWith("departmentgroupcode=")
        || lower.startsWith("departmentgroupid=")
        || lower.startsWith("referenceabledepartmentgroupcodes=")
        || lower.startsWith("referenceabledepartmentgroupids=");
  }

  private resetCloseConfirmation(): void {
    WindowCloser.closeConfirmation = async () => true;
    window.onbeforeunload = null;
  }

  private async setupWindowRectangle(to: Route): Promise<void> {
    GeometryManager.unregisterAllManagers();  // 遷移前に登録されてたハンドラは全て破棄.
    if (this.shouldRestoreGeometries(to)) {
      GeometryManager.restore(to.path);
    } else {
      this.logger.debug("位置・サイズの復元処理をスキップしました。");
    }
  }

  private shouldRestoreGeometries(to: Route): boolean {
    if (!to.meta) { return true; }
    // ログイン未了の場合、userId等はリダイレクト時にuserCodeに直されるのでuserCodeのみの判定で足りている
    return !to.meta.disableGeometryRestoration
        && !to.meta.isRedirect
        && !to.meta.isBackground
        && !(to.meta.isLogin && !!to.query.userCode);
  }

  private updateWindowTitle(title: string): void {
    if (!title) { return; }
    document.title = title;
  }
}
